import { CommonCMS, ShoppingBag } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { useErrorToast } from "@lib/utils/store-utility/storeUtility";
import { useUpdateCart } from "@lib/commerce/hooks/checkoutOperations";
import type { ResourceIdentifierInput } from "@graphql/generated/types";
import { buildSupplyChannelActions } from "@lib/utils/checkout-utility/supplyChannelActions";
import { executeCommerceToolsWithRetry } from "@lib/utils/executeCommerceToolsWithRetry";
import { Text } from "@ecco/ui/text";
import { FormControl } from "@ecco/ui/form-control";
import { Radio, RadioGroup } from "@ecco/ui/radio";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { isEmpty } from "lodash-es";
import type { FC, JSX } from "react";
import { useEffect, useMemo, useState } from "react";
import { useStore } from "@store/index";
import type { CheckoutFormInitialValues, DeliveryStepFormProps } from "../types";
import { useCheckout } from "@lib/utils/checkout-utility/checkoutUtility";
import { ShippingMethodTypes } from "@features/checkout/types";
import { DeliveryFee } from "./DeliveryFee";
import { getDeliveryMethodKey } from "@lib/utils/findDeliveryMethods";
import { activeSiteConfig } from "@config/site/site-config";
import { DeliveryMethodWrapper } from "./DeliveryMethodWrapper";
import { RadioButtonPickUpPoint } from "./RadioButtonPickUpPoint";
import { CarrierIcon } from "./carrier-icon/CarrierIcon";
import { useLocale } from "next-intl";
import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import { CheckoutProgressSubtypes } from "@lib/analytics/analyticsTypes";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import { type GraphQLError } from "graphql";
import {
    DELIVERY_METHOD_RADIO_CC_TEST_ID,
    DELIVERY_METHOD_RADIO_HD_TEST_ID,
    DELIVERY_METHOD_RADIO_PS_TEST_ID,
    DELIVERY_METHOD_WRAPPER_TEST_ID,
} from "./constants";
import { useFormikContext } from "formik";
import { hasGraphQLErrors } from "@lib/utils/graphql/hasGraphQLErrors";
import type { ShippingPrice } from "@components/commerce/order/types";
import {
    useEstimatedDeliveryDate,
    useExpectedDeliveryText,
    useIsEstimatedDeliveryDateLoading,
} from "@hooks/useEstimatedDeliveryDate";
import { DeliveryEstimate } from "./DeliveryEstimate";
import {
    EXPRESS_FULFILLMENT_OPTION,
    useDisplayedShippingMethods,
} from "./hooks/useDisplayedShippingMethods";
import { getExpressDeliveryEstimate } from "./utils/shouldHideExpressDelivery";
import {
    ESTIMATED_DELIVERY_HEADING,
    ESTIMATED_DELIVERY_HEADING_VB,
    formatEstimatedDeliveryDate,
} from "@lib/utils/pdp/estimatedDelivery";

export interface DeliveryMethodsProps {
    shippingMethods: DeliveryStepFormProps["shippingMethods"];
    error?: any;
    isDisabled?: boolean;
    formId?: string;
    isInvalid?: boolean;
    deliveryMethodId: string;
}

export const DeliveryMethods: FC<DeliveryMethodsProps> = ({
    shippingMethods,
    formId,
    error,
    isInvalid,
    deliveryMethodId,
}): JSX.Element => {
    const { setFieldValue, values } = useFormikContext<CheckoutFormInitialValues>();
    const locale = useLocale();
    const executeUpdateCart = useUpdateCart();
    const getCart = useStore((state) => state.getCart);
    const setStoreData = useStore((state) => state.setStoreData);
    const storeData = useStore((state) => state.data.storeData);
    const setParcelShopData = useStore((state) => state.setParcelShopData);
    const parcelShopData = useStore((state) => state.data.parcelShopData);
    const errorToaster = useErrorToast();
    const { getMultiple } = useMicrocopy();
    const [hasChanged, setHasChanged] = useState<boolean>(false);

    const logSelectMethod = (methodKey: string, methodData: Record<string, unknown>) => {
        Logger.info(ServiceType.WEB, `Delivery Method Selected: ${methodKey}`, {
            deliveryMethod: methodKey,
            data: methodData,
            tag: LogTag.SHIPPING,
        });
    };

    const handleDeliveryMethodError = ({
        method,
        supplyChannelErrors,
        deliveryMethodErrors,
    }: {
        method: ShippingPrice;
        deliveryMethodErrors?: GraphQLError[];
        supplyChannelErrors?: GraphQLError[];
    }) => {
        errorToaster({
            title: microcopies[CommonCMS.error.general.title],
        });
        Logger.error(ServiceType.WEB, `Error setting delivery method: ${method.key}`, {
            deliveryMethod: method,
            deliveryMethodErrors,
            supplyChannelErrors,
            tag: LogTag.SHIPPING,
        });
    };

    const { addDeliveryMethod } = useCheckout();

    const microcopies = getMultiple(CommonCMS.shared, [
        CommonCMS.error.general.title,
        CommonCMS.error.general.description,
    ]);

    const CCKey = getDeliveryMethodKey(ShippingMethodTypes.CLICK_AND_COLLECT);
    const isPickUpMethod = (deliveryMethodId: string) => {
        return (
            deliveryMethodId === getDeliveryMethodKey(ShippingMethodTypes.CLICK_AND_COLLECT) ||
            deliveryMethodId === getDeliveryMethodKey(ShippingMethodTypes.PARCEL_SHOP)
        );
    };

    const getMethodName = (methodKey: string): string => {
        const selectedMethod = shippingMethods?.find((m) => m.key === methodKey);
        return selectedMethod?.localizedName?.[locale] ?? selectedMethod?.name ?? methodKey;
    };

    useEffect(() => {
        if (!isEmpty(storeData)) {
            setFieldValue(formId, CCKey);
            setFieldValue("hasSameBillingAddress", false);
            setHasChanged(true);
        }
    }, [storeData]);

    useEffect(() => {
        if (!isEmpty(parcelShopData)) {
            setFieldValue(formId, ShippingMethodTypes.PARCEL_SHOP);
            setFieldValue("hasSameBillingAddress", false);
            setHasChanged(true);
        }
    }, [parcelShopData]);

    // Set/clear the channel on every line item using the current cart version; the
    // shared retry util recovers from any genuine ConcurrentModification.
    const setLineSupplyChannel = async (
        supplyChannel: ResourceIdentifierInput | Record<string, never>
    ) => {
        const cart = getCart();
        return await executeCommerceToolsWithRetry(
            executeUpdateCart,
            {
                cartId: cart.id,
                cartVersion: cart.version,
                storeKey: process.env.NEXT_PUBLIC_STORE_KEY,
                locale: locale,
                actions: buildSupplyChannelActions(cart.lineItems, supplyChannel),
            },
            0,
            "DeliveryMethods setAllLineItemsSupplyChannel"
        );
    };

    const onSelectBasicMethod = async (method: ShippingPrice) => {
        const previousMethod = deliveryMethodId;
        const isPreviousMethodPickup = isPickUpMethod(previousMethod);

        // If we go from a pickup method to a basic method we should clear the address fields so it doesn't contain the address of the pickup location.
        if (isPreviousMethodPickup) {
            const fieldsToClear = [
                "streetName",
                "postalCode",
                "region",
                "additionalAddressInfo",
                "city",
                "landmark",
            ];
            // Clear all address fields without showing an error. The user didn't do anything wrong here, so not need to scream at them.
            fieldsToClear.forEach((field) => {
                setFieldValue(`deliveryAddress.${field}`, "", false);
            });
        }

        // Set the new field value without validating.
        setFieldValue(formId, method.key, false);
        setHasChanged(true);

        const { errors: supplyChannelErrors } = await setLineSupplyChannel({});
        const { errors: deliveryMethodErrors } = await addDeliveryMethod(method.key);

        if (hasGraphQLErrors(supplyChannelErrors, deliveryMethodErrors)) {
            handleDeliveryMethodError({
                method,
                supplyChannelErrors,
                deliveryMethodErrors,
            });
        }
    };

    const estimatedDeliveryDate = useEstimatedDeliveryDate();
    const expectedDeliveryText = useExpectedDeliveryText(estimatedDeliveryDate);
    // Drives the skeleton in expectedDeliveryText below.
    const isEstimatedDeliveryDateLoading = useIsEstimatedDeliveryDateLoading();

    const { displayedMethods, expressMethod } = useDisplayedShippingMethods({
        shippingMethods,
        postalCode: values.deliveryAddress?.postalCode,
        deliveryMethodId,
        onAutoSelect: onSelectBasicMethod,
    });

    // Express has no cart-based estimate from the delivery-estimate API - its date is derived
    // straight from its own "N-M business days" copy, so this is synchronous (no loading state).
    const expressEstimatedDeliveryDate = useMemo(() => {
        const range = getExpressDeliveryEstimate(expressMethod?.deliveryTime?.[locale]);
        return range ? formatEstimatedDeliveryDate(range, locale) : null;
    }, [expressMethod, locale]);
    const expressExpectedDeliveryText = useExpectedDeliveryText(expressEstimatedDeliveryDate);

    const expectedDeliveryLabelVB = useExpectedDeliveryText(
        estimatedDeliveryDate,
        ShoppingBag.expectedDeliveryVB
    );
    const expressExpectedDeliveryLabelVB = useExpectedDeliveryText(
        expressEstimatedDeliveryDate,
        ShoppingBag.expectedDeliveryVB
    );

    useEffect(() => {
        if (hasChanged) {
            setHasChanged(false);
            switch (deliveryMethodId) {
                case ShippingMethodTypes.PARCEL_SHOP: {
                    setStoreData(null);
                    if (parcelShopData) {
                        logSelectMethod(deliveryMethodId, {
                            parcelShop: parcelShopData,
                        });
                        // Fire analytics event for Parcel Shop selection (user selected via parcel shop picker)
                        AnalyticsClient.checkoutProgress(
                            CheckoutProgressSubtypes.deliveryMethodSelected,
                            getMethodName(ShippingMethodTypes.PARCEL_SHOP),
                            "user"
                        );
                    }
                    break;
                }
                case CCKey: {
                    setParcelShopData(null);
                    if (storeData) {
                        logSelectMethod(deliveryMethodId, {
                            storeData: storeData,
                        });
                        // Fire analytics event for Click & Collect selection (user selected via store picker)
                        AnalyticsClient.checkoutProgress(
                            CheckoutProgressSubtypes.deliveryMethodSelected,
                            getMethodName(CCKey),
                            "user"
                        );
                    }
                    break;
                }
                default: {
                    setStoreData(null);
                    setParcelShopData(null);

                    logSelectMethod(deliveryMethodId, {});
                    AnalyticsClient.checkoutProgress(
                        CheckoutProgressSubtypes.deliveryMethodSelected,
                        getMethodName(deliveryMethodId),
                        "user"
                    );
                    break;
                }
            }
        }
    }, [deliveryMethodId]);

    return (
        <FormControl isRequired isInvalid={isInvalid} data-testid={DELIVERY_METHOD_WRAPPER_TEST_ID}>
            <RadioGroup name={formId} value={deliveryMethodId}>
                <Box
                    display={["flex", "grid"]}
                    gridTemplateColumns="1fr"
                    flexDirection="row"
                    flexWrap="wrap"
                    gap={3}
                >
                    {displayedMethods?.map((method) => {
                        const deliveryTime = method.deliveryTime[locale];
                        const deliveryPrice = method.deliveryFee ?? method.price?.totalNet;
                        const name = method.localizedName?.[locale] ?? method.name;
                        // The raw carrier copy (e.g. "3-4 business days") is only a fallback for
                        // when we can't show an actual estimated date. Once we can, showing both
                        // is redundant, so the estimate (or its loading skeleton) takes over.
                        const showsDeliveryEstimate =
                            method.fulfillmentOption === EXPRESS_FULFILLMENT_OPTION
                                ? Boolean(expressExpectedDeliveryText)
                                : (method.fulfillmentOption === "HD" ||
                                      method.fulfillmentOption === "PS") &&
                                  (Boolean(expectedDeliveryText) || isEstimatedDeliveryDateLoading);
                        switch (method.key) {
                            case CCKey:
                                return (
                                    <DeliveryMethodWrapper
                                        key={method.key}
                                        error={error}
                                        data-testid={DELIVERY_METHOD_RADIO_CC_TEST_ID}
                                    >
                                        <RadioButtonPickUpPoint
                                            method={method}
                                            storeData={storeData}
                                            locale={locale}
                                        />
                                    </DeliveryMethodWrapper>
                                );
                            case ShippingMethodTypes.PARCEL_SHOP:
                                return (
                                    <DeliveryMethodWrapper
                                        key={method.key}
                                        error={error}
                                        data-testid={DELIVERY_METHOD_RADIO_PS_TEST_ID}
                                    >
                                        <RadioButtonPickUpPoint
                                            method={method}
                                            storeData={parcelShopData}
                                            locale={locale}
                                        />
                                    </DeliveryMethodWrapper>
                                );
                            case ShippingMethodTypes.TRY_AND_BUY:
                                return null;
                            default:
                                return (
                                    <DeliveryMethodWrapper
                                        key={method.key}
                                        onClick={(e) => {
                                            // Prevent additional click events from children
                                            e.stopPropagation();
                                            if (method.key === deliveryMethodId) {
                                                // Already selected, abort
                                                return;
                                            }
                                            onSelectBasicMethod(method);
                                        }}
                                        error={error}
                                        data-testid={DELIVERY_METHOD_RADIO_HD_TEST_ID}
                                    >
                                        <Flex
                                            flexDirection={"row"}
                                            justifyContent={"space-between"}
                                            alignItems={"center"}
                                        >
                                            <Radio
                                                aria-label={name}
                                                value={method.key}
                                                bg={"white"}
                                                tabIndex={0}
                                                display={"flex"}
                                                alignItems={"center"}
                                                height="auto"
                                                flex={1}
                                                onKeyDown={(e) => {
                                                    // Change the delivery method on Enter or Space key press
                                                    if (e.key === "Enter" || e.key === " ") {
                                                        e.preventDefault();
                                                        onSelectBasicMethod(method);
                                                    }
                                                }}
                                            >
                                                <Flex
                                                    justifyContent={"flex-start"}
                                                    alignItems={"center"}
                                                    gap={2}
                                                    flex={1}
                                                >
                                                    <CarrierIcon
                                                        name={
                                                            method.hasMultipleIcons
                                                                ? "multipleIcons"
                                                                : method.carrierCode?.toLowerCase()
                                                        }
                                                    />
                                                    <Flex gap={1} flexDir="column">
                                                        {name && (
                                                            <Text fontWeight={"bold"}>{name}</Text>
                                                        )}
                                                        {deliveryTime && !showsDeliveryEstimate && (
                                                            <Text size="small">{deliveryTime}</Text>
                                                        )}
                                                        {(method.fulfillmentOption === "HD" ||
                                                            method.fulfillmentOption === "PS") && (
                                                            <>
                                                                <Flex
                                                                    data-ab-id={
                                                                        ESTIMATED_DELIVERY_HEADING
                                                                    }
                                                                >
                                                                    <DeliveryEstimate
                                                                        label={expectedDeliveryText}
                                                                        isLoading={
                                                                            isEstimatedDeliveryDateLoading
                                                                        }
                                                                    />
                                                                </Flex>
                                                                <Flex
                                                                    data-ab-id={
                                                                        ESTIMATED_DELIVERY_HEADING_VB
                                                                    }
                                                                    className="ab-hide"
                                                                >
                                                                    <DeliveryEstimate
                                                                        label={
                                                                            expectedDeliveryLabelVB
                                                                        }
                                                                        isLoading={
                                                                            isEstimatedDeliveryDateLoading
                                                                        }
                                                                    />
                                                                </Flex>
                                                            </>
                                                        )}
                                                        {method.fulfillmentOption ===
                                                            EXPRESS_FULFILLMENT_OPTION && (
                                                            <>
                                                                <Flex
                                                                    data-ab-id={
                                                                        ESTIMATED_DELIVERY_HEADING
                                                                    }
                                                                >
                                                                    <DeliveryEstimate
                                                                        label={
                                                                            expressExpectedDeliveryText
                                                                        }
                                                                    />
                                                                </Flex>
                                                                <Flex
                                                                    data-ab-id={
                                                                        ESTIMATED_DELIVERY_HEADING_VB
                                                                    }
                                                                    className="ab-hide"
                                                                >
                                                                    <DeliveryEstimate
                                                                        label={
                                                                            expressExpectedDeliveryLabelVB
                                                                        }
                                                                    />
                                                                </Flex>
                                                            </>
                                                        )}
                                                    </Flex>
                                                </Flex>
                                            </Radio>
                                            {activeSiteConfig.checkout.hasShippingFees &&
                                                deliveryPrice && (
                                                    <Flex width="100px" justifyContent="flex-end">
                                                        <DeliveryFee
                                                            price={deliveryPrice}
                                                            locale={locale}
                                                        />
                                                    </Flex>
                                                )}
                                        </Flex>
                                    </DeliveryMethodWrapper>
                                );
                        }
                    })}
                </Box>
            </RadioGroup>
        </FormControl>
    );
};
